import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import { ArrowUp } from 'lucide-mui';
import CircularProgress from '@mui/material/CircularProgress';

import { FilePreviewRouteParams } from '../types';
import { askQuestion } from '../selectedVideoSlice';
import { RootState } from '../../../reducers';
import { useLimits } from '../../plan/hooks/limits';
import ChatLimitReached from './history/ChatLimitReached';

export default function CustomizedInputBase({
  chatBoxRef,
}: {
  chatBoxRef: React.RefObject<HTMLDivElement>;
}) {
  const [question, setQuestion] = React.useState('');
  const theme = useTheme();
  const guardLimits = useLimits();
  const showLimitReached = guardLimits('askAI') <= 0;
  const { fileId } = useParams<FilePreviewRouteParams>();
  const { status } = useSelector(
    (state: RootState) => state.selectedVideo.chat
  );
  const dispatch = useDispatch();

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const query = event.target.value;
    setQuestion(query);
  };

  const handleSearch = (event: React.FormEvent | React.KeyboardEvent) => {
    event.preventDefault();
    if (!fileId || !question || showLimitReached) return;
    guardLimits('askAI', () => {
      dispatch(
        askQuestion({
          fileId,
          question,
        })
      );
    });
    setTimeout(() => {
      if (chatBoxRef.current) {
        chatBoxRef.current.scrollTo({
          top: chatBoxRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 300);

    setQuestion('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSearch(event);
    }
  };

  return (
    <>
      {showLimitReached && <ChatLimitReached />}
      <Paper
        component="form"
        onSubmit={handleSearch}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          boxShadow: 'none',
          border: `1px solid ${theme.palette.secondary.main}`,
          borderRadius: '8px',
          backgroundColor: theme.palette.background.default,
          position: 'relative',
        }}
      >
        <InputBase
          sx={{
            ml: 1,
            flex: 1,
            height: 'auto',
            minHeight: '2.5rem',
            paddingRight: '40px', // Adjust padding if necessary
          }}
          placeholder="Ask AI about this recording"
          inputProps={{ 'aria-label': 'ask ai', multiline: true }}
          value={question}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={status === 'pending'}
          multiline
        />
        {question && status !== 'pending' && (
          <IconButton
            type="submit"
            sx={{
              position: 'absolute',
              bottom: '4px',
              right: '4px',
              zIndex: 1,
              backgroundColor: theme.palette.primary.main,
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: theme.palette.primary.light,
                '& .MuiSvgIcon-root': {
                  transform: 'translateY(-2px)',
                },
              },
            }}
          >
            <ArrowUp
              sx={{
                transition: 'transform 0.2s ease-in-out',
                color: theme.palette.primary.contrastText,
              }}
            />
          </IconButton>
        )}
        {status === 'pending' && (
          <CircularProgress
            size="1rem"
            sx={{
              color: theme.palette.secondary.dark,
              position: 'absolute',
              bottom: '12px',
              right: '12px',
            }}
          />
        )}
      </Paper>
    </>
  );
}
